import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './DesignEditor.css';
import ShirtPreviewModal from '../ShirtPreviewModal/ShirtPreviewModal';

// Imprint location sizes (inches)
const LOCATION_SIZES_RECTANGLE = {
  'Full Front':          { w: 11.7, h: 16.5  },
  'Medium Front':        { w: 8.27, h: 11.69 },
  'Center Chest':        { w: 5,    h: 5     },
  'Across Chest':        { w: 11.7, h: 4     },
  'Left Chest':          { w: 4,    h: 4     },
  'Right Chest':         { w: 4,    h: 4     },
  'Left Vertical':       { w: 4,    h: 14.75 },
  'Right Vertical':      { w: 4,    h: 14.75 },
  'Bottom Left':         { w: 4,    h: 6     },
  'Bottom Right':        { w: 4,    h: 6     },
  'Full Back':           { w: 11.7, h: 14.75 },
  'Medium Back':         { w: 8.27, h: 11.69 },
  'Locker Center Patch': { w: 4,    h: 4     },
  'Across Shoulder':     { w: 8,    h: 11.7  },
  'Left Sleeve':         { w: 3.5,  h: 3.5   },
  'Right Sleeve':        { w: 3.5,  h: 3.5   },
  'Back Center':         { w: 8,    h: 11.5  },
};
const LOCATION_SIZES_CIRCLE = {
  'Full Front':          { w: 11.5, h: 11.5 },
  'Medium Front':        { w: 9,    h: 9    },
  'Center Chest':        { w: 5,    h: 5    },
  'Across Chest':        { w: 10,   h: 4    },
  'Left Chest':          { w: 4,    h: 4    },
  'Right Chest':         { w: 4,    h: 4    },
  'Left Vertical':       { w: 4,    h: 11.5 },
  'Right Vertical':      { w: 4,    h: 11.5 },
  'Bottom Left':         { w: 4,    h: 6    },
  'Bottom Right':        { w: 4,    h: 6    },
  'Full Back':           { w: 11.5, h: 11.5 },
  'Medium Back':         { w: 9,    h: 9    },
  'Locker Center Patch': { w: 4,    h: 4    },
  'Back Center':         { w: 8,    h: 11.5 },
  'Left Sleeve':         { w: 3.5,  h: 3.5  },
  'Right Sleeve':        { w: 3.5,  h: 3.5  },
};

const CANVAS_PX = 320;
const MAX_INCH  = 16.5;
const inchToPx  = (inch) => Math.round((inch / MAX_INCH) * CANVAS_PX);

function getBoundaryPx(loc, shape) {
  const table = shape === 'circle' ? LOCATION_SIZES_CIRCLE : LOCATION_SIZES_RECTANGLE;
  const size  = table[loc] || { w: 11.7, h: 16.5 };
  return { w: inchToPx(size.w), h: inchToPx(size.h) };
}

const INITIAL_TRANSFORM = { x: 0, y: 0, w: 180, h: 180, rotation: 0, flipH: false };

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function hasTransparency(data) {
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
}

function detectEdgeBackgroundColor(data, width, height) {
  const bins = new Map();
  const add = (x, y, weight = 1) => {
    const p = (y * width + x) * 4;
    const a = data[p + 3];
    if (a <= 16) return;
    const qr = Math.round(data[p] / 12);
    const qg = Math.round(data[p + 1] / 12);
    const qb = Math.round(data[p + 2] / 12);
    const key = qr + '|' + qg + '|' + qb;
    const cur = bins.get(key) || { count: 0, r: 0, g: 0, b: 0 };
    cur.count += weight;
    cur.r += data[p] * weight;
    cur.g += data[p + 1] * weight;
    cur.b += data[p + 2] * weight;
    bins.set(key, cur);
  };

  const cornerSample = Math.max(4, Math.floor(Math.min(width, height) * 0.12));
  const sampleCorner = (startX, startY) => {
    for (let y = startY; y < startY + cornerSample; y += 1) {
      for (let x = startX; x < startX + cornerSample; x += 1) {
        add(x, y, 3);
      }
    }
  };

  sampleCorner(0, 0);
  sampleCorner(Math.max(0, width - cornerSample), 0);
  sampleCorner(0, Math.max(0, height - cornerSample));
  sampleCorner(Math.max(0, width - cornerSample), Math.max(0, height - cornerSample));

  // Keep border strip sampling too, but lower weight than corners.
  for (let x = 0; x < width; x += 1) {
    add(x, 0, 1);
    add(x, height - 1, 1);
  }
  for (let y = 0; y < height; y += 1) {
    add(0, y, 1);
    add(width - 1, y, 1);
  }

  let best = null;
  for (const entry of bins.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  if (!best || best.count < 8) return null;
  return {
    r: Math.round(best.r / best.count),
    g: Math.round(best.g / best.count),
    b: Math.round(best.b / best.count),
  };
}

function isBackgroundLike(data, idx, bg, tolerance) {
  const a = data[idx + 3];
  if (a <= 8) return false;
  const dr = data[idx] - bg.r;
  const dg = data[idx + 1] - bg.g;
  const db = data[idx + 2] - bg.b;
  const distSq = dr * dr + dg * dg + db * db;
  return distSq <= tolerance * tolerance;
}

function removeBackgroundByComponents(ctx, width, height, bgColor, options = {}) {
  const {
    removeLargeInner = false,
    tolerance = 56,
  } = options;

  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const total = width * height;
  const visited = new Uint8Array(total);
  const queue = new Uint32Array(total); // reused for each component
  const component = new Uint32Array(total); // reused for each component
  let removed = 0;
  const norm = Math.max(0, Math.min(1, (tolerance - 28) / 82));
  const minInnerArea = Math.max(8, Math.floor(total * (0.005 - (norm * 0.0046))));

  const tryPush = (x, y, tailRef) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return;
    const p = y * width + x;
    if (visited[p]) return;
    const idx = p * 4;
    if (!isBackgroundLike(data, idx, bgColor, tolerance)) return;
    visited[p] = 1;
    queue[tailRef.value++] = p;
  };

  for (let start = 0; start < total; start += 1) {
    if (visited[start]) continue;
    const startIdx = start * 4;
    if (!isBackgroundLike(data, startIdx, bgColor, tolerance)) continue;

    let head = 0;
    const tailRef = { value: 0 };
    let compLen = 0;
    let touchesEdge = false;
    const sx = start % width;
    const sy = (start - sx) / width;
    tryPush(sx, sy, tailRef);

    while (head < tailRef.value) {
      const p = queue[head++];
      component[compLen++] = p;
      const x = p % width;
      const y = (p - x) / width;
      if (x === 0 || y === 0 || x === width - 1 || y === height - 1) {
        touchesEdge = true;
      }
      tryPush(x - 1, y, tailRef);
      tryPush(x + 1, y, tailRef);
      tryPush(x, y - 1, tailRef);
      tryPush(x, y + 1, tailRef);
    }

    const shouldRemove = touchesEdge || (removeLargeInner && compLen >= minInnerArea);
    if (!shouldRemove) continue;

    for (let i = 0; i < compLen; i += 1) {
      const idx = component[i] * 4 + 3;
      if (data[idx] !== 0) {
        data[idx] = 0;
        removed += 1;
      }
    }
  }

  if (removed > 0) {
    ctx.putImageData(imageData, 0, 0);
  }
  return { removed, imageData };
}

function removeColorHaloOnEdges(imageData, bgColor, tolerance) {
  const data = imageData.data;
  let changed = 0;
  const haloTol = Math.max(10, Math.round(tolerance * 0.72));
  const haloTolSq = haloTol * haloTol;

  for (let i = 0; i < data.length; i += 4) {
    const aByte = data[i + 3];
    if (aByte <= 0 || aByte >= 255) continue;
    const dr = data[i] - bgColor.r;
    const dg = data[i + 1] - bgColor.g;
    const db = data[i + 2] - bgColor.b;
    const distSq = dr * dr + dg * dg + db * db;
    // Only decontaminate semi-transparent pixels that still look like bg fringing.
    if (distSq > haloTolSq) continue;
    const a = aByte / 255;
    const inv = 1 - a;
    data[i] = Math.max(0, Math.min(255, Math.round((data[i] - (bgColor.r * inv)) / a)));
    data[i + 1] = Math.max(0, Math.min(255, Math.round((data[i + 1] - (bgColor.g * inv)) / a)));
    data[i + 2] = Math.max(0, Math.min(255, Math.round((data[i + 2] - (bgColor.b * inv)) / a)));
    changed += 1;
  }

  return changed;
}

async function prepareUploadedImage(sourceURL, mimeType) {
  const img = await loadImageElement(sourceURL);
  const width = Math.max(img.naturalWidth || img.width, 1);
  const height = Math.max(img.naturalHeight || img.height, 1);
  const aspect = width / height;

  if (mimeType === 'image/svg+xml') {
    return { url: sourceURL, aspect };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const data = ctx.getImageData(0, 0, width, height).data;
  const hasAlpha = hasTransparency(data);

  // Keep original upload untouched; removal is applied only during preview/save render.
  if (hasAlpha) return { url: sourceURL, aspect };
  return { url: sourceURL, aspect };
}

async function getRenderableImageSource(imageURL, removeWhiteBackground, bgTolerance) {
  const img = await loadImageElement(imageURL);
  if (!removeWhiteBackground) return img;

  const width = Math.max(img.naturalWidth || img.width, 1);
  const height = Math.max(img.naturalHeight || img.height, 1);
  const offscreen = document.createElement('canvas');
  offscreen.width = width;
  offscreen.height = height;
  const offCtx = offscreen.getContext('2d');
  offCtx.drawImage(img, 0, 0, width, height);
  const srcData = offCtx.getImageData(0, 0, width, height).data;

  const bgColor = detectEdgeBackgroundColor(srcData, width, height);
  if (!bgColor) return offscreen;

  // Remove edge-connected background and large enclosed islands close to detected edge color.
  const result = removeBackgroundByComponents(offCtx, width, height, bgColor, {
    removeLargeInner: true,
    tolerance: bgTolerance,
  });
  if (result.removed > 0) {
    removeColorHaloOnEdges(result.imageData, bgColor, bgTolerance);
    offCtx.putImageData(result.imageData, 0, 0);
  }
  return offscreen;
}

// Canvas render helper
// KEY FIX: canvas stays transparent - no white background fill
// This ensures the design PNG keeps its transparency when passed to ShirtPreviewModal
async function renderToCanvas(imageURL, boundary, transform, isCircle, removeWhiteBackground, bgTolerance) {
  const canvas = document.createElement('canvas');
  canvas.width  = boundary.w;
  canvas.height = boundary.h;
  const ctx = canvas.getContext('2d');

  // NO fillRect white here - keep transparent!

  if (isCircle) {
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(boundary.w / 2, boundary.h / 2, boundary.w / 2, boundary.h / 2, 0, 0, Math.PI * 2);
    ctx.clip();
  }

  const img = await getRenderableImageSource(imageURL, removeWhiteBackground, bgTolerance);
  const sourceW = Math.max(img.naturalWidth || img.width, 1);
  const sourceH = Math.max(img.naturalHeight || img.height, 1);
  const scaleX = transform.w / sourceW;
  const scaleY = scaleX; // lock aspect ratio

  ctx.save();
  const appliedScaleX = transform.flipH ? -scaleX : scaleX;
  ctx.translate(boundary.w / 2 + transform.x, boundary.h / 2 + transform.y);
  ctx.rotate((transform.rotation * Math.PI) / 180);
  ctx.scale(appliedScaleX, scaleY);
  ctx.drawImage(img, -sourceW / 2, -sourceH / 2);
  ctx.restore();

  if (isCircle) ctx.restore();

  return canvas;
}

// Component
function DesignEditor() {
  const navigate       = useNavigate();
  const routerLocation = useLocation();

  const {
    sideLabel        = 'Front',
    imprintLocation  = '',
    allSideLocations = [],
    productConfig    = {},
    existingDesigns  = {},
    rawOriginalURLs  = {},
    originalURLs     = {},
    savedTransforms  = {},
    editorSettings   = {},
    currentStep      = 0,
  } = routerLocation.state || {};

  const designShape = productConfig?.selectedShape || 'rectangle';
  const isCircle    = designShape === 'circle';
  const shirtColor  = productConfig?.selectedColor || '';

  const sideLocations = useMemo(() => allSideLocations.length > 0
    ? allSideLocations
    : (imprintLocation ? [imprintLocation] : []), [allSideLocations, imprintLocation]);

  const [activeLocation, setActiveLocation] = useState(imprintLocation || sideLocations[0] || '');

  const [locStates, setLocStates] = useState(() => {
    const init = {};
    sideLocations.forEach(loc => {
      const isActiveUpload = loc === imprintLocation && routerLocation.state?.file instanceof File;
      const uploadedFile   = isActiveUpload ? routerLocation.state.file : null;
      const uploadedURL    = isActiveUpload ? routerLocation.state.previewURL || null : null;
      const existing       = existingDesigns[loc];

      const rawSource =
        uploadedURL ||
        (routerLocation.state?.rawOriginalURLs?.[loc]) ||
        originalURLs[loc] ||
        (typeof existing === 'string' ? existing : null);

      const savedT     = savedTransforms[loc];
      const isRestored = !!(savedT?._restored);
      const hasRawSource = !!rawSource;

      let restoredTransform;
      if (savedT && hasRawSource && (isRestored || !('_restored' in savedT))) {
        const aspect = savedT._naturalAspect || null;
        const baseT  = { ...INITIAL_TRANSFORM, ...savedT };
        restoredTransform = aspect
          ? { ...baseT, h: Math.round(baseT.w / aspect) }
          : baseT;
      } else {
        restoredTransform = { ...INITIAL_TRANSFORM };
      }

      init[loc] = {
        imageURL:      rawSource,
        originalURL:   rawSource,
        naturalAspect: savedT?._naturalAspect || null,
        file:          uploadedFile || (existing instanceof File ? existing : null),
        transform:     restoredTransform,
        history:       [restoredTransform],
        historyIdx:    0,
        // sentinel: if true, useEffect will skip re-initializing this loc
        _restored:     isRestored,
      };
    });
    return init;
  });
  // Load natural aspect ratio - skip fully restored locations
  useEffect(() => {
    sideLocationsRef.current.forEach(loc => {
      const ls = locStatesRef.current[loc];

      // KEY FIX: skip if restored from savedTransforms - transform already correct
      if (ls?._restored) return;
      if (ls?.naturalAspect !== null && ls?.naturalAspect !== undefined) return;

      if (!ls?.imageURL && ls?.file instanceof File) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const url = e.target.result;
          const img = new Image();
          img.onload = () => {
            const aspect = img.naturalWidth / img.naturalHeight;
            const initW  = 180;
            const initH  = Math.round(initW / aspect);
            const t      = { ...INITIAL_TRANSFORM, w: initW, h: initH };
            setLocStates(prev => ({
              ...prev,
              [loc]: { ...prev[loc], imageURL: url, originalURL: url, naturalAspect: aspect, transform: t, history: [t], historyIdx: 0, _restored: false },
            }));
          };
          img.src = url;
        };
        reader.readAsDataURL(ls.file);
        return;
      }

      if (!ls?.imageURL) return;

      const img = new Image();
      img.onload = () => {
        const aspect = img.naturalWidth / img.naturalHeight;
        setLocStates(prev => {
          const cur = prev[loc];
          if (!cur || cur._restored) return prev;
          const fixedH         = Math.round(cur.transform.w / aspect);
          const fixedTransform = { ...cur.transform, h: fixedH };
          return {
            ...prev,
            [loc]: { ...cur, naturalAspect: aspect, transform: fixedTransform, history: [fixedTransform], historyIdx: 0 },
          };
        });
      };
      img.src = ls.imageURL;
    });
  }, []);

  const activeLoc = locStates[activeLocation] || {
    imageURL: null, naturalAspect: null, file: null,
    transform: { ...INITIAL_TRANSFORM }, history: [{ ...INITIAL_TRANSFORM }], historyIdx: 0, _restored: false,
  };
  const t = activeLoc.transform;

  const setLocState = useCallback((loc, updater) => {
    setLocStates(prev => {
      const cur = prev[loc];
      if (!cur) return prev;
      return { ...prev, [loc]: typeof updater === 'function' ? updater(cur) : { ...cur, ...updater } };
    });
  }, []);

  const pushHistory = useCallback((loc, newTransform) => {
    setLocState(loc, (cur) => {
      const trimmed = cur.history.slice(0, cur.historyIdx + 1);
      return { ...cur, transform: newTransform, history: [...trimmed, newTransform], historyIdx: cur.historyIdx + 1, _restored: false };
    });
  }, [setLocState]);

  const fileInputRef  = useRef(null);
  const pendingLocRef = useRef(null);

  const triggerUpload = (loc) => { pendingLocRef.current = loc; fileInputRef.current?.click(); };

  const handleFileSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !pendingLocRef.current) return;
    const loc = pendingLocRef.current;
    pendingLocRef.current = null;
    e.target.value = '';

    const isImg = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'].includes(f.type);
    const url   = isImg ? URL.createObjectURL(f) : null;

    if (url) {
      try {
        const prepared = await prepareUploadedImage(url, f.type);
        const aspect = prepared.aspect || null;
        const initW = 180;
        const initH = aspect ? Math.round(initW / aspect) : 180;
        const initTransform = { ...INITIAL_TRANSFORM, w: initW, h: initH };
        setLocState(loc, {
          imageURL: prepared.url,
          originalURL: prepared.url,
          naturalAspect: aspect,
          file: f,
          transform: initTransform,
          history: [initTransform],
          historyIdx: 0,
          _restored: false,
        });
        setActiveLocation(loc);
        if (prepared.url !== url) URL.revokeObjectURL(url);
      } catch (err) {
        setLocState(loc, {
          imageURL: url,
          originalURL: url,
          naturalAspect: null,
          file: f,
          transform: { ...INITIAL_TRANSFORM },
          history: [{ ...INITIAL_TRANSFORM }],
          historyIdx: 0,
          _restored: false,
        });
        setActiveLocation(loc);
      }
    } else {
      setLocState(loc, { imageURL: null, originalURL: null, naturalAspect: null, file: f, transform: { ...INITIAL_TRANSFORM }, history: [{ ...INITIAL_TRANSFORM }], historyIdx: 0, _restored: false });
      setActiveLocation(loc);
    }
  };

  const handleUndo = () => setLocState(activeLocation, cur => {
    if (cur.historyIdx <= 0) return cur;
    const idx = cur.historyIdx - 1;
    return { ...cur, historyIdx: idx, transform: cur.history[idx] };
  });
  const handleRedo = () => setLocState(activeLocation, cur => {
    if (cur.historyIdx >= cur.history.length - 1) return cur;
    const idx = cur.historyIdx + 1;
    return { ...cur, historyIdx: idx, transform: cur.history[idx] };
  });
  const handleRotate  = () => pushHistory(activeLocation, { ...t, rotation: (t.rotation + 90) % 360 });
  const handleFlipH   = () => pushHistory(activeLocation, { ...t, flipH: !t.flipH });
  const handleZoomIn  = () => {
    const newW = Math.min(t.w * 1.15, 800);
    const newH = activeLoc.naturalAspect ? Math.round(newW / activeLoc.naturalAspect) : Math.min(t.h * 1.15, 800);
    pushHistory(activeLocation, { ...t, w: newW, h: newH });
  };
  const handleZoomOut = () => {
    const newW = Math.max(t.w * 0.87, 20);
    const newH = activeLoc.naturalAspect ? Math.round(newW / activeLoc.naturalAspect) : Math.max(t.h * 0.87, 20);
    pushHistory(activeLocation, { ...t, w: newW, h: newH });
  };
  const handleReset = () => {
    const initW = 180;
    const initH = activeLoc.naturalAspect ? Math.round(initW / activeLoc.naturalAspect) : 180;
    pushHistory(activeLocation, { ...INITIAL_TRANSFORM, w: initW, h: initH });
  };

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const loc    = activeLocation;
    const startX = (e.touches ? e.touches[0].clientX : e.clientX) - t.x;
    const startY = (e.touches ? e.touches[0].clientY : e.clientY) - t.y;
    const onMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      setLocState(loc, cur => ({ ...cur, transform: { ...cur.transform, x: cx - startX, y: cy - startY }, _restored: false }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend',  onUp);
      setLocState(loc, cur => {
        const trimmed = cur.history.slice(0, cur.historyIdx + 1);
        return { ...cur, history: [...trimmed, cur.transform], historyIdx: cur.historyIdx + 1 };
      });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend',  onUp);
  }, [t.x, t.y, activeLocation, setLocState]);

  const handleResizeStart = useCallback((e, corner) => {
    e.preventDefault(); e.stopPropagation();
    const loc    = activeLocation;
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startW = t.w;
    const aspect = activeLoc.naturalAspect || (t.w / Math.max(t.h, 1));
    const onMove = (ev) => {
      const cx   = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const dx   = cx - startX;
      const newW = Math.max(30, corner === 'se' || corner === 'ne' ? startW + dx : startW - dx);
      const newH = Math.round(newW / aspect);
      setLocState(loc, cur => ({ ...cur, transform: { ...cur.transform, w: newW, h: newH }, _restored: false }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend',  onUp);
      setLocState(loc, cur => {
        const trimmed = cur.history.slice(0, cur.historyIdx + 1);
        return { ...cur, history: [...trimmed, cur.transform], historyIdx: cur.historyIdx + 1 };
      });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend',  onUp);
  }, [t, activeLoc.naturalAspect, activeLocation, setLocState]);

  const boundary = getBoundaryPx(activeLocation, designShape);
  const renderedH = activeLoc.naturalAspect ? Math.round(t.w / activeLoc.naturalAspect) : t.h;

  const locStatesRef = useRef(locStates);
  const sideLocationsRef = useRef(sideLocations);
  useEffect(() => { locStatesRef.current = locStates; }, [locStates]);
  useEffect(() => { sideLocationsRef.current = sideLocations; }, [sideLocations]);

  // Load natural aspect ratio - skip fully restored locations
  useEffect(() => {
    sideLocationsRef.current.forEach(loc => {
      const ls = locStatesRef.current[loc];

      // KEY FIX: skip if restored from savedTransforms - transform already correct
      if (ls?._restored) return;
      if (ls?.naturalAspect !== null && ls?.naturalAspect !== undefined) return;

      if (!ls?.imageURL && ls?.file instanceof File) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const url = e.target.result;
          const img = new Image();
          img.onload = () => {
            const aspect = img.naturalWidth / img.naturalHeight;
            const initW  = 180;
            const initH  = Math.round(initW / aspect);
            const t      = { ...INITIAL_TRANSFORM, w: initW, h: initH };
            setLocStates(prev => ({
              ...prev,
              [loc]: { ...prev[loc], imageURL: url, originalURL: url, naturalAspect: aspect, transform: t, history: [t], historyIdx: 0, _restored: false },
            }));
          };
          img.src = url;
        };
        reader.readAsDataURL(ls.file);
        return;
      }

      if (!ls?.imageURL) return;

      const img = new Image();
      img.onload = () => {
        const aspect = img.naturalWidth / img.naturalHeight;
        setLocStates(prev => {
          const cur = prev[loc];
          if (!cur || cur._restored) return prev;
          const fixedH         = Math.round(cur.transform.w / aspect);
          const fixedTransform = { ...cur.transform, h: fixedH };
          return {
            ...prev,
            [loc]: { ...cur, naturalAspect: aspect, transform: fixedTransform, history: [fixedTransform], historyIdx: 0 },
          };
        });
      };
      img.src = ls.imageURL;
    });
  }, []);

  // Preview modal
  const [showPreview,    setShowPreview]    = useState(false);
  const [previewMode,    setPreviewMode]    = useState('current');
  const [previewDesigns, setPreviewDesigns] = useState({});
  const [removeWhiteBg,  setRemoveWhiteBg]  = useState(
    typeof editorSettings?.removeBackground === 'boolean' ? editorSettings.removeBackground : true
  );
  const [bgTolerance,    setBgTolerance]    = useState(
    Number.isFinite(Number(editorSettings?.backgroundTolerance))
      ? Math.max(28, Math.min(110, Number(editorSettings.backgroundTolerance)))
      : 62
  );

  const openPreview = useCallback(async (mode) => {
    setPreviewMode(mode);
    const toRender = mode === 'current' ? [activeLocation] : sideLocations;
    const results  = {};
    for (const loc of toRender) {
      const ls = locStates[loc];
      if (!ls?.originalURL) continue;
      const bnd    = getBoundaryPx(loc, designShape);
      const canvas = await renderToCanvas(ls.originalURL, bnd, ls.transform, isCircle, removeWhiteBg, bgTolerance);
      // toDataURL with 'image/png' preserves transparency
      results[loc] = canvas.toDataURL('image/png');
    }
    setPreviewDesigns(results);
    setShowPreview(true);
  }, [activeLocation, sideLocations, locStates, designShape, isCircle, removeWhiteBg, bgTolerance]);
  // handleSaveChanges
  const handleSaveChanges = useCallback(async () => {
    const updatedDesigns      = { ...existingDesigns };
    const preservedOriginals  = { ...rawOriginalURLs };
    const preservedTransforms = { ...savedTransforms };
    const bakedPreviews       = {};

    for (const loc of sideLocations) {
      const ls = locStates[loc];
      if (!ls?.originalURL) continue;

      const bnd          = getBoundaryPx(loc, designShape);
      const canvas       = await renderToCanvas(ls.originalURL, bnd, ls.transform, isCircle, removeWhiteBg, bgTolerance);
      const bakedDataURL = canvas.toDataURL('image/png');

      await new Promise(resolve => {
        canvas.toBlob(blob => {
          if (blob) {
            updatedDesigns[loc] = new File(
              [blob],
              'design-' + loc.replace(/\s+/g, '-') + '.png',
              { type: 'image/png' }
            );
          } else if (ls.file) {
            updatedDesigns[loc] = ls.file;
          }
          resolve();
        }, 'image/png');
      });

      preservedOriginals[loc]  = ls.originalURL;
      preservedTransforms[loc] = {
        ...ls.transform,
        _naturalAspect: ls.naturalAspect || null,
        _restored:      true,
      };
      bakedPreviews[loc] = bakedDataURL;
    }

    navigate('/product-details', {
      state: {
        product:         productConfig?.product,
        productConfig,
        updatedDesigns,
        rawOriginalURLs: preservedOriginals,
        originalURLs:    bakedPreviews,
        savedTransforms: preservedTransforms,
        editorSettings: {
          removeBackground: removeWhiteBg,
          backgroundTolerance: bgTolerance,
        },
        currentStep,
      },
    });
  }, [locStates, sideLocations, designShape, isCircle, removeWhiteBg, bgTolerance, existingDesigns, rawOriginalURLs, savedTransforms, productConfig, navigate, currentStep]);

  const canUndo = (activeLoc.historyIdx || 0) > 0;
  const canRedo = (activeLoc.historyIdx || 0) < ((activeLoc.history?.length || 1) - 1);

  const imgStyle = {
    position:    'absolute',
    left:        'calc(50% + ' + t.x + 'px)',
    top:         'calc(50% + ' + t.y + 'px)',
    width:       t.w,
    height:      renderedH,
    transform:   'translate(-50%, -50%) rotate(' + t.rotation + 'deg) scaleX(' + (t.flipH ? -1 : 1) + ')',
    cursor:      'grab',
    userSelect:  'none',
    touchAction: 'none',
  };

  return (
    <div className="design-editor-page">
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.svg,.pdf,.ai,.psd"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />

      <div className="design-editor-wrapper">

        {/* SIDEBAR */}
        <div className="editor-sidebar">
          <div className="sidebar-info">
            <span className="sidebar-side-badge">{sideLabel}</span>
            <p className="sidebar-hint">Editing <strong>{sideLabel}</strong> designs.</p>
          </div>

          {sideLocations.length > 0 && (
            <div className="sidebar-location-tabs">
              <span className="sidebar-tabs-heading">Locations - Click here to switch</span>
              {sideLocations.map(loc => {
                const hasImg = !!locStates[loc]?.imageURL;
                return (
                  <button
                    key={loc}
                    className={'sidebar-loc-tab ' + (activeLocation === loc ? 'active' : '') + ' ' + (hasImg ? 'has-img' : '')}
                    onClick={() => setActiveLocation(loc)}
                    title={loc}
                  >
                    <span className="loc-tab-dot" />
                    <span className="loc-tab-name">{loc}</span>
                    {hasImg && <span className="loc-tab-check">&#10003;</span>}
                  </button>
                );
              })}
            </div>
          )}

          {!activeLoc.imageURL && (
            <button className="sidebar-btn upload-loc-btn" onClick={() => triggerUpload(activeLocation)}>
              &uarr; Upload for {activeLocation}
            </button>
          )}

          <div className="sidebar-shape-info">
            <span className="shape-label">Shape</span>
            <span className="shape-value">{isCircle ? 'Circle' : 'Rectangle'}</span>
          </div>
          <div className="sidebar-shape-info">
            <span className="shape-label">Background</span>
            <label className="shape-value" style={{ display: 'inline-flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={removeWhiteBg}
                onChange={(e) => setRemoveWhiteBg(e.target.checked)}
              />
              {removeWhiteBg ? 'Remove' : 'Keep'}
            </label>
          </div>
          {removeWhiteBg && (
            <div className="sidebar-shape-info" style={{ display: 'block' }}>
              <span className="shape-label">Tolerance</span>
              <div className="shape-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="range"
                  min={28}
                  max={110}
                  step={1}
                  value={bgTolerance}
                  onChange={(e) => setBgTolerance(Number(e.target.value))}
                  style={{ width: 110 }}
                />
                <span>{bgTolerance}</span>
              </div>
            </div>
          )}
          <div className="sidebar-spacer" />

          <button className="sidebar-btn preview-btn" onClick={() => openPreview('current')} disabled={!activeLoc.imageURL}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            Preview This
          </button>

          {sideLocations.length > 1 && (
            <button className="sidebar-btn preview-btn preview-all-btn" onClick={() => openPreview('all')}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
              Preview All
            </button>
          )}

          <button className="sidebar-btn save-changes" onClick={handleSaveChanges}>
            &#10003; Save Changes
          </button>
        </div>

        {/* EDITOR PANEL */}
        <div className="editor-panel">
          <div className="editor-panel-header">
            <div className="editor-title-row">
              <h1>Design Editor</h1>
              <span className="editor-active-loc-badge">{activeLocation}</span>
            </div>
            <div className="undo-redo-row">
              <button className="undo-redo-btn" onClick={handleUndo} disabled={!canUndo} title="Undo">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M3 7v6h6"/><path d="M3 13C5 7.5 10 4 16 4a9 9 0 0 1 0 18c-4 0-7.5-2-9-5"/>
                </svg>
                Undo
              </button>
              <button className="undo-redo-btn" onClick={handleRedo} disabled={!canRedo} title="Redo">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 7v6h-6"/><path d="M21 13c-2 5.5-7 9-13 9a9 9 0 0 1 0-18c4 0 7.5 2 9 5"/>
                </svg>
                Redo
              </button>
            </div>
          </div>

          <div className="canvas-outer">
            <div className="canvas-size-label">
              <span>{activeLocation}</span>
              <span className="canvas-dims">{boundary.w}x{boundary.h}px</span>
            </div>

            <div
              className={'print-area ' + (isCircle ? 'circle' : 'rectangle')}
              style={{ width: boundary.w, height: boundary.h }}
            >
              <div
                className="print-area-clip"
                style={{ width: boundary.w, height: boundary.h, borderRadius: isCircle ? '50%' : 0 }}
              >
                {activeLoc.imageURL ? (
                  <img
                    src={activeLoc.imageURL}
                    alt="design"
                    className="design-image-draggable"
                    style={imgStyle}
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                    draggable={false}
                  />
                ) : (
                  <div className="empty-canvas-prompt" onClick={() => triggerUpload(activeLocation)}>
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="12" y1="18" x2="12" y2="12"/>
                      <line x1="9"  y1="15" x2="15" y2="15"/>
                    </svg>
                    <span>Click to upload<br/><small>{activeLocation}</small></span>
                  </div>
                )}
              </div>

              {activeLoc.imageURL && ['nw', 'ne', 'sw', 'se'].map(corner => (
                <div
                  key={corner}
                  className={'resize-handle ' + corner}
                  style={{
                    left: 'calc(50% + ' + t.x + 'px ' + (corner.includes('e') ? '+ ' + (t.w / 2) + 'px' : '- ' + (t.w / 2) + 'px') + ')',
                    top:  'calc(50% + ' + t.y + 'px ' + (corner.includes('s') ? '+ ' + (renderedH / 2) + 'px' : '- ' + (renderedH / 2) + 'px') + ')',
                  }}
                  onMouseDown={(e) => handleResizeStart(e, corner)}
                  onTouchStart={(e) => handleResizeStart(e, corner)}
                />
              ))}
            </div>

            <p className="canvas-hint">Drag to reposition - Drag corners to resize</p>
            <p className="editor-quality-note">
              Remove BG removes solid-color background only (white or black).
              If unchecked, no background removal is applied and the uploaded image stays as-is.
              <br />
              Tolerance controls removal strength: lower = safer, higher = more aggressive.
              <br/>
              For better quality, recommended to upload an image with transparent/no background,
              especially for complex designs.
            </p>
          </div>

          <div className="editor-toolbar">
            <button className="toolbar-btn" onClick={handleRotate} title="Rotate 90deg">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
              <span className="toolbar-label">Rotate</span>
            </button>
            <button className={'toolbar-btn ' + (t.flipH ? 'active' : '')} onClick={handleFlipH} title="Flip">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="12" y1="3" x2="12" y2="21"/>
                <polyline points="7,6 3,12 7,18"/>
                <polyline points="17,6 21,12 17,18"/>
              </svg>
              <span className="toolbar-label">Flip</span>
            </button>
            <button className="toolbar-btn" onClick={handleZoomIn} title="Zoom In">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="10" cy="10" r="6"/><line x1="15" y1="15" x2="21" y2="21"/>
                <line x1="10" y1="7" x2="10" y2="13"/><line x1="7" y1="10" x2="13" y2="10"/>
              </svg>
              <span className="toolbar-label">Zoom In</span>
            </button>
            <button className="toolbar-btn" onClick={handleZoomOut} title="Zoom Out">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="10" cy="10" r="6"/><line x1="15" y1="15" x2="21" y2="21"/>
                <line x1="7" y1="10" x2="13" y2="10"/>
              </svg>
              <span className="toolbar-label">Zoom Out</span>
            </button>
            <button className="toolbar-btn" onClick={handleReset} title="Reset">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
              <span className="toolbar-label">Reset</span>
            </button>
          </div>
        </div>
      </div>

      {/* PREVIEW MODAL */}
      <ShirtPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        designImages={previewDesigns}
        isCircle={isCircle}
        shirtColor={shirtColor}
        title={previewMode === 'all' ? 'Preview - All ' + sideLabel + ' Locations' : 'Preview - ' + activeLocation}
        onSave={handleSaveChanges}
      />
    </div>
  );
}

export default DesignEditor;


